import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "./AuthProvider"
import { supabase } from "../lib/supabase"
import { ROUTES } from "../lib/routes"

function daysRemaining(endsAt: string): number {
  return Math.max(0, Math.ceil((new Date(endsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
}

export function TrialBanner() {
  const { companyId, role } = useAuth()
  const navigate = useNavigate()
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null)
  const [hasSubscription, setHasSubscription] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!companyId || role === "creator") return
    supabase
      .from("companies")
      .select("trial_ends_at, stripe_subscription_id")
      .eq("id", companyId)
      .single()
      .then(({ data }: { data: { trial_ends_at: string | null; stripe_subscription_id: string | null } | null }) => {
        if (!data) return
        setTrialEndsAt(data.trial_ends_at ?? null)
        setHasSubscription(!!data.stripe_subscription_id)
      })
  }, [companyId, role])

  if (!trialEndsAt || hasSubscription || dismissed) return null

  const days = daysRemaining(trialEndsAt)
  if (days <= 0) return null // expired — router guard handles redirect

  const isUrgent = days <= 2
  const colorClass = isUrgent
    ? "bg-rose-500/10 border-rose-400/20 text-rose-300"
    : "bg-amber-500/10 border-amber-400/20 text-amber-300"
  const dotClass = isUrgent ? "bg-rose-400" : "bg-amber-400"

  return (
    <div className={`pointer-events-auto flex items-center gap-2 px-3 py-1.5 rounded-xl border font-mono text-[11px] backdrop-blur-xl ${colorClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass} animate-pulse`} />
      <span className="uppercase tracking-wider">
        TRIAL · {days} DAY{days !== 1 ? "S" : ""} REMAINING
      </span>
      <button
        type="button"
        onClick={() => navigate(ROUTES.billing)}
        className="ml-1 uppercase tracking-wider hover:text-white transition-colors duration-150"
      >
        UPGRADE →
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="ml-1 opacity-40 hover:opacity-80 transition-opacity duration-150"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  )
}
